import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import StatusPill from "../components/StatusPill";
import { Sparkles, LogOut, PlusCircle } from "lucide-react";

const SERVICES = [
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "deep", l: "Deep Cleaning" },
  { v: "move_in", l: "Move-in" },
  { v: "move_out", l: "Move-out" },
  { v: "recurring", l: "Recurring" },
];

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    service_type: "residential", preferred_date: "", preferred_time: "09:00",
    address: "", notes: "",
  });

  async function load() {
    const { data } = await api.get("/portal/jobs");
    setJobs(data);
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    await api.post("/portal/request", form);
    setOpen(false);
    setForm({ service_type: "residential", preferred_date: "", preferred_time: "09:00", address: "", notes: "" });
    load();
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white"/>
            </div>
            <div className="font-display font-semibold">DK-Elite</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-600 hidden sm:block">Hi, {user?.name}</div>
            <button onClick={async () => { await logout(); nav("/login"); }}
              data-testid="portal-logout"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md px-3 py-1.5">
              <LogOut className="w-4 h-4"/> Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="relative h-[280px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1758273238594-9a9d6c20eaa2?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent"/>
        <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center text-white">
          <div className="text-xs uppercase tracking-[0.3em] text-blue-200 mb-3">Customer Portal</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight max-w-2xl">
            Book a sparkle, track your bookings.
          </h1>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-semibold">My Bookings</h2>
          <button onClick={() => setOpen(true)} data-testid="portal-request-btn"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm px-4 py-2 rounded-md transition-colors">
            <PlusCircle className="w-4 h-4"/> Request Service
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {jobs.length === 0 ? (
            <div className="p-12 text-center" data-testid="portal-empty">
              <div className="text-slate-500 mb-4">You haven't booked any cleanings yet.</div>
              <button onClick={() => setOpen(true)} className="text-sm text-blue-600 hover:underline">Request your first service →</button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Service</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Address</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((j) => (
                  <tr key={j.id} data-testid={`portal-job-${j.id}`}>
                    <td className="px-6 py-3 capitalize font-medium">{j.service_type.replace("_", " ")}</td>
                    <td className="px-6 py-3 text-slate-600">{j.date} · {j.time}</td>
                    <td className="px-6 py-3 text-slate-600">{j.address || "—"}</td>
                    <td className="px-6 py-3"><StatusPill status={j.status}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(ev) => ev.stopPropagation()} data-testid="portal-request-modal">
            <h3 className="font-display text-xl font-semibold mb-4">Request a service</h3>
            <form onSubmit={submit} className="space-y-3">
              <select data-testid="pr-service" value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
                {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input required type="date" data-testid="pr-date" value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
                <input type="time" data-testid="pr-time" value={form.preferred_time}
                  onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
              </div>
              <input required placeholder="Address" data-testid="pr-address" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
              <textarea placeholder="Notes (special instructions, access info)" rows={3} data-testid="pr-notes"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500"/>
              <button type="submit" data-testid="pr-submit"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2.5 rounded-md transition-colors">
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
